import { Controller } from "@hotwired/stimulus"

// Le tableau de cascade des paramètres d'une capacité (Insurance::Catalog::FieldsComponent, phase 2, « Ce qui se
// paramètre »).
//
// - Détail technique d'une ligne : la ligne qui la suit porte un <details class="insurance-catalog-tech"> (masquée par le
//   CSS tant qu'il est fermé). Le chevron de la cellule Paramètre l'ouvre ou le ferme (`toggleDetail`, aria-controls) ;
//   l'événement `toggle` de chaque <details> (`detailToggled`) recale le chevron. Chevron et détail sont de la couche
//   technique ([data-catalog-tech], details.insurance-catalog-tech) : l'interrupteur « Détails techniques » les affiche,
//   repliés, ou les cache (lot J1a) ; il ne les ouvre pas.
// - Compression des étages (fiche d'un fork) : les étages sans valeur propre portent is-collapsible, masqués tant que le
//   tableau est is-compressed. « N étages masqués · Afficher » (`toggleLevels`) les montre ou les masque, et les cellules
//   qui couvrent les étages (ligne fermée, détail technique) prennent le colspan qui va (data-colspan-compressed,
//   data-colspan-expanded). L'état est gardé par tableau (l'adresse de rechargement de sa frame : capacité et fork)
//   tant que l'application reste chargée : un rechargement du tableau (enregistrement d'une étape dans le panneau
//   latéral) ne remasque pas ce qu'on venait d'afficher.

// Tableaux dont les étages masqués ont été affichés.
const expandedTables = new Set()

export default class extends Controller {
  static targets = ["levelsToggle", "levelsLabel"]
  static values = { frame: String }

  connect() {
    if (expandedTables.has(this.tableKey)) this.applyLevels(true)
  }

  // Le même id de frame sert sur chaque fork : la clé est l'adresse de rechargement, qui porte le fork.
  get tableKey() {
    return this.element.closest("turbo-frame")?.dataset.reloadUrl || this.frameValue
  }

  // ── Détail technique ───────────────────────────────────────────────────────────────────────

  toggleDetail(event) {
    const button = event.currentTarget
    const details = this.detailsFor(button)
    if (!details) return

    event.preventDefault()
    details.open = !details.open
  }

  detailToggled(event) {
    const details = event.currentTarget
    for (const button of this.element.querySelectorAll(`[aria-controls="${CSS.escape(details.id)}"]`)) {
      button.setAttribute("aria-expanded", details.open ? "true" : "false")
    }
  }

  detailsFor(button) {
    const id = button.getAttribute("aria-controls")
    const details = id ? document.getElementById(id) : null
    return details instanceof HTMLDetailsElement && this.element.contains(details) ? details : null
  }

  // ── Étages masqués ─────────────────────────────────────────────────────────────────────────

  toggleLevels(event) {
    event.preventDefault()
    const expanded = this.element.classList.contains("is-compressed")
    if (expanded) expandedTables.add(this.tableKey)
    else expandedTables.delete(this.tableKey)
    this.applyLevels(expanded)
  }

  applyLevels(expanded) {
    this.element.classList.toggle("is-compressed", !expanded)
    for (const cell of this.element.querySelectorAll("[data-colspan-compressed]")) {
      cell.colSpan = Number(expanded ? cell.dataset.colspanExpanded : cell.dataset.colspanCompressed) || 1
    }
    if (!this.hasLevelsToggleTarget) return

    const toggle = this.levelsToggleTarget
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (this.hasLevelsLabelTarget) {
      this.levelsLabelTarget.textContent = expanded ? toggle.dataset.shownLabel : toggle.dataset.hiddenLabel
    }
  }
}
